import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import type { ModalProps } from "../types/components.js";

export function Modal({
  open,
  title,
  controlId,
  closeLabel,
  children,
  onClose,
  size = "default",
}: ModalProps) {
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [onClose, open]);

  if (!open) return null;
  const titleId = `${controlId}-title`;
  const widthClass = size === "wide" ? "max-w-5xl" : size === "medium" ? "max-w-2xl" : "max-w-lg";
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/20 p-4 backdrop-blur-sm"
      role="presentation"
    >
      <section
        aria-labelledby={titleId}
        aria-modal="true"
        className={`max-h-[90vh] w-full overflow-auto rounded-lg border border-border bg-popover p-5 text-popover-foreground shadow-lg sm:p-6 ${widthClass}`}
        role="dialog"
      >
        <header className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-lg font-medium" id={titleId}>
            {title}
          </h2>
          <button
            aria-label={closeLabel}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
            data-control-id={`${controlId}.close`}
            onClick={onClose}
            ref={closeButton}
            type="button"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
